import { useState, useEffect } from "react";
import { client } from "../lib/sanity";
import { urlForImageWithVanity } from "../lib/sanityImageUrl";
import { DEFAULT_LANGUAGE, isApprovedVariant } from "./languageUtils";

export const useSanityBlogPosts = (limit = 3, currentLang = DEFAULT_LANGUAGE) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPosts = async () => {
      try {
        const query = `*[_type == "post"] | order(publishedAt desc)[0...${limit}] {
          _id,
          title,
          slug,
          excerpt,
          localeVariants[]{language, title, slug, excerpt, status, indexable},
          mainImage{asset->{_id, url, originalFilename, metadata{dimensions{width, height}}}, caption, alt},
          publishedAt,
          categories[]->{title, slug},
          claps,
          readTime,
          "bodyText": pt::text(body),
          seo{metaTitle, metaDescription, openGraph{title, description, image{asset->{url, originalFilename}, alt}}}
        }`;
        const sanityPosts = await client.fetch(query);
        const localizedPosts = sanityPosts
          .map((post) => {
            const variant = (post.localeVariants || []).find(
              (candidate) => candidate.language === currentLang && isApprovedVariant(candidate)
            );
            if (!variant && currentLang !== DEFAULT_LANGUAGE) return null;
            return variant ? { ...post, title: variant.title || post.title, excerpt: variant.excerpt || post.excerpt, slug: variant.slug || post.slug, locale: currentLang } : { ...post, locale: DEFAULT_LANGUAGE };
          })
          .filter(Boolean);
        const enhancedPosts = localizedPosts.map((post) => ({
          ...post,
          mainImage: post.mainImage ? { ...post.mainImage, enhancedUrl: urlForImageWithVanity(post.mainImage) } : null,
          seo: post.seo ? { ...post.seo, openGraph: post.seo.openGraph ? { ...post.seo.openGraph, image: post.seo.openGraph.image ? { ...post.seo.openGraph.image, enhancedUrl: urlForImageWithVanity(post.seo.openGraph.image) } : null } : null } : null,
        }));
        if (!cancelled) setPosts(enhancedPosts);
      } catch (err) {
        if (!cancelled) { setError(err.message); console.error("Error fetching posts:", err); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    fetchPosts();
    return () => { cancelled = true; };
  }, [limit, currentLang]);

  return { posts, loading, error };
};
